import { designVariables } from "@studio3t/design-variables";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { externalRenameAction, type RenamePhase } from "./renameModel";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { RowName } from "./stageRowChrome";

// Inline row-name renaming shared by the schema and transform rows.
// Double-clicking the name edits it inline: Enter/blur commit, Escape
// cancels. A taken name blocks Enter (red border + message in the
// tooltip); blurring an invalid name cancels instead. Committing keeps
// the input up but DISABLED for the request's duration; success closes it
// onto the already-renamed row. A failure holds the disabled input (error
// treatment) for 5s, then re-enables it with the typed name intact for
// another attempt.

interface RenameOptions {
  name: string;
  dirty: boolean;
  testId: string;
  /** Persists the new name; resolves false when the save failed. */
  onRename: (name: string) => Promise<boolean>;
  /** Duplicate check — an error message, or null when the name is free. */
  validateRename: (name: string) => string | null;
  /** Ran on the double-click before the input opens; returning true
   *  swallows the edit (the caller surfaced its own flow instead). */
  interceptEdit?: () => boolean;
  /** Re-selects the row if the double-click's FIRST click deselected it
   *  (only the second click is swallowed below — on an already-selected
   *  row the first one toggles it off, orphaning the editor: keystrokes
   *  then miss the row's draft and unstaging can't clear the content). */
  ensureSelected?: () => void;
  /** Stops the label from stretching (RowName's $fit) so a trailing badge
   *  sits right after the text instead of at the row's far edge. */
  fit?: boolean;
  /** Overrides the label's dirty star without touching the save gating —
   *  an untitled draft renamed to a real name reads clean even though it
   *  is still unsaved. Defaults to `dirty`. */
  star?: boolean;
  /** Mounts with the input already OPEN on the current name — a freshly
   *  minted untitled row's first-naming affordance. Hands-off: focus
   *  stays with the editor the user is typing into (the code editor
   *  whose keystroke minted the script, the assertion editor "Write a
   *  test" opened); clicking the input focuses it. Mount-time only. */
  startEditing?: boolean;
  /** The row's selection state. Leaving the row closes an open input —
   *  the hands-off naming open never blurs (it may not hold focus), so
   *  without this it would linger on the deselected row. Omitted =
   *  selection doesn't drive the input. */
  selected?: boolean;
  /** Re-opens the input (hands-off again) whenever the row is selected —
   *  the untitled rows' standing naming affordance while they still wear
   *  their default "Untitled …" name. */
  reopenOnSelect?: boolean;
}

interface RenameState {
  value: string | null;
  phase: RenamePhase;
  error: string | null;
  trimmed: string;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  /** Whether the open input took focus (a startEditing open leaves it
   *  with the editor). */
  focusOpen: boolean;
  edit: (value: string | null) => void;
  commit: () => Promise<void>;
  /** Opens the input (keeping any typed value) showing this error,
   *  focused — a direct row Save's duplicate-name/failure feedback. */
  openWithError: (message: string) => void;
}

// The duplicate-check verdict for the typed value: only a non-empty,
// actually-changed name gets validated.
function renameErrorFor(
  options: RenameOptions,
  value: string | null,
  trimmed: string,
): string | null {
  if (value === null) return null;
  if (trimmed === "") return null;
  if (trimmed === options.name) return null;
  return options.validateRename(trimmed);
}

// A commit with nothing to persist — unchanged, blank or invalid name —
// simply cancels the edit.
function isNoopCommit(
  trimmed: string,
  name: string,
  error: string | null,
): boolean {
  if (!trimmed) return true;
  if (trimmed === name) return true;
  return error !== null;
}

// A REJECTED write (a failed network call) fails the rename exactly like
// a resolved false — without this the phase would stay "saving" forever,
// leaving the input disabled on an unhandled rejection.
async function attemptRename(
  options: RenameOptions,
  trimmed: string,
): Promise<boolean> {
  try {
    return await options.onRename(trimmed);
  } catch {
    return false;
  }
}

// The external-rename verdict applied to the input state: "close" drops
// the open value and any error; "keep" leaves everything.
function applyExternalRename(
  action: "close" | "keep",
  setValue: (value: string | null) => void,
  setForcedError: (error: string | null) => void,
) {
  if (action !== "close") return;
  setValue(null);
  setForcedError(null);
}

// The 5s failure hold: the disabled input keeps the typed name, then
// re-enables and refocuses for another attempt.
function holdFailure(
  setPhase: (phase: RenamePhase) => void,
  inputRef: React.MutableRefObject<HTMLInputElement | null>,
) {
  setPhase("failed");
  window.setTimeout(() => {
    setPhase("idle");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, 5000);
}

// What a selection change does to the input: entering a `reopenOnSelect`
// row re-opens it, leaving any row closes an idle open input, everything
// else leaves the input alone (a mid-request close lands when the phase
// returns to idle). Rows that don't pass `selected` opt out entirely.
function selectionAction(
  selected: boolean | undefined,
  reopenOnSelect: boolean | undefined,
  phase: RenamePhase,
): "reopen" | "close" | "none" {
  if (selected === undefined) return "none";
  if (selected) return reopenOnSelect ? "reopen" : "none";
  return phase === "idle" ? "close" : "none";
}

// Applies the selection verdict to the input state: "reopen" brings the
// naming input back hands-off (like the mint-time open), "close" cancels
// the open edit.
function applySelectionAction(
  action: "reopen" | "close" | "none",
  input: {
    setValue: React.Dispatch<React.SetStateAction<string | null>>;
    setFocusOpen: (open: boolean) => void;
    setForcedError: (message: string | null) => void;
    name: string;
  },
) {
  if (action === "reopen") {
    input.setFocusOpen(false);
    input.setValue((prev) => prev ?? input.name);
  }
  if (action === "close") {
    input.setForcedError(null);
    input.setValue(null);
  }
}

// The rename field's state machine: typed value, request phase and the
// commit flow (no-op commits cancel; failures hold for 5s then refocus).
function useRenameState(options: RenameOptions): RenameState {
  // A fresh row renders its input open from the FIRST frame (no label
  // flash) — hands-off, so it never competes with the editor's focus.
  const [value, setValue] = useState<string | null>(
    options.startEditing ? options.name : null,
  );
  // Whether the open input grabs focus: the mount-time open above never
  // does; any user-initiated edit takes it.
  const [focusOpen, setFocusOpen] = useState(!options.startEditing);
  const [phase, setPhase] = useState<RenamePhase>("idle");
  // An error pushed from OUTSIDE the rename flow — a direct row Save
  // rejected by a duplicate name or a failed write paints the input red
  // with its message. Any edit clears it.
  const [forcedError, setForcedError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const trimmed = value?.trim() ?? "";
  const error = forcedError ?? renameErrorFor(options, value, trimmed);
  const edit = useCallback((next: string | null) => {
    setForcedError(null);
    setFocusOpen(true);
    setValue(next);
  }, []);

  // Opens the input (keeping any typed value) in the error state,
  // focused so the user can fix the name right away.
  const nameRef = useRef(options.name);
  nameRef.current = options.name;
  const openWithError = useCallback((message: string) => {
    setForcedError(message);
    setFocusOpen(true);
    setValue((prev) => prev ?? nameRef.current);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  // An external rename (the dropped source's seed naming the draft after
  // its collection) closes a hands-off naming input — the stale default
  // it shows would otherwise flag red against the new name.
  const externallyRenamedRef = useRef(options.name);
  useEffect(() => {
    if (externallyRenamedRef.current === options.name) return;
    externallyRenamedRef.current = options.name;
    applyExternalRename(
      externalRenameAction(value !== null, focusOpen, phase),
      setValue,
      setForcedError,
    );
  }, [options.name, value, focusOpen, phase]);

  // Selection-driven open/close: a focused input already commits on the
  // blur a row switch causes; this covers the hands-off naming open that
  // never held focus (it would otherwise linger on the deselected row).
  useEffect(() => {
    applySelectionAction(
      selectionAction(options.selected, options.reopenOnSelect, phase),
      { setValue, setFocusOpen, setForcedError, name: nameRef.current },
    );
  }, [options.selected, options.reopenOnSelect, phase]);

  const commit = async () => {
    if (value === null || phase !== "idle") return;
    if (isNoopCommit(trimmed, options.name, error)) {
      setForcedError(null);
      setValue(null);
      return;
    }
    setPhase("saving");
    if (await attemptRename(options, trimmed)) {
      setPhase("idle");
      setValue(null);
      return;
    }
    holdFailure(setPhase, inputRef);
  };

  return {
    value,
    phase,
    error,
    trimmed,
    inputRef,
    focusOpen,
    edit,
    commit,
    openWithError,
  };
}

// The resting name label; a double-click swaps it for the input (unless
// the caller's intercept surfaced its own flow instead).
function RenameLabel({
  options,
  rename,
}: {
  options: RenameOptions;
  rename: RenameState;
}) {
  const { editsLocked } = useStudioPipeline();
  return (
    <RowName
      $fit={options.fit}
      // The second click of a double-click must not re-toggle the row —
      // the first one already selected it.
      onClickCapture={(e) => {
        if (e.detail > 1) e.stopPropagation();
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        options.ensureSelected?.();
        // A view-only studio (editsLocked) never opens the input.
        if (editsLocked || options.interceptEdit?.()) return;
        rename.edit(options.name);
      }}
    >
      {options.name}
      {(options.star ?? options.dirty) ? "*" : ""}
    </RowName>
  );
}

// Enter commits (unless the name is taken), Escape cancels; the row
// itself activates on Enter/Space, so typing must not bubble.
function onRenameKeyDown(rename: RenameState, e: React.KeyboardEvent) {
  e.stopPropagation();
  if (e.key === "Escape") rename.edit(null);
  if (e.key === "Enter" && rename.error === null) rename.commit();
}

// Disabling the input mid-request also blurs it — only an idle blur means
// the user left the field: an invalid name cancels, a valid one commits.
function onRenameBlur(rename: RenameState) {
  if (rename.phase !== "idle") return;
  if (rename.error) rename.edit(null);
  else rename.commit();
}

// The failure hold explains itself; otherwise the duplicate-name message
// rides the input's tooltip.
function renameTitle(rename: RenameState): string | undefined {
  return rename.phase === "failed"
    ? "Renaming failed — try again in a moment"
    : (rename.error ?? undefined);
}

// The open rename input with its commit/cancel keyboard and blur rules.
function RenameField({
  options,
  rename,
}: {
  options: RenameOptions;
  rename: RenameState;
}) {
  return (
    <RenameInput
      ref={rename.inputRef}
      // A fresh-mint open may deliberately leave focus in the code
      // editor (focusOpen false); clicking the input focuses it then.
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={rename.focusOpen}
      value={rename.value ?? ""}
      disabled={rename.phase !== "idle"}
      $invalid={rename.error !== null || rename.phase === "failed"}
      title={renameTitle(rename)}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => rename.edit(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => onRenameKeyDown(rename, e)}
      onBlur={() => onRenameBlur(rename)}
      data-testid={options.testId}
    />
  );
}

// Save disables while the typed name is empty or taken (the input's red
// state), while the request runs, and through the failure hold — and,
// outside a rename, while the row has nothing to persist.
function renameSaveDisabled(dirty: boolean, rename: RenameState): boolean {
  return (
    (!dirty && rename.value === null) ||
    rename.phase !== "idle" ||
    rename.error !== null ||
    (rename.value !== null && rename.trimmed === "")
  );
}

export function useInlineRename(options: RenameOptions) {
  const rename = useRenameState(options);
  const nameNode =
    rename.value === null ? (
      <RenameLabel options={options} rename={rename} />
    ) : (
      <RenameField options={options} rename={rename} />
    );
  return {
    nameNode,
    saveDisabled: renameSaveDisabled(options.dirty, rename),
    /** The name a direct row Save should use: the open input's text when
     *  there is any, the committed name otherwise (default "Untitled …"
     *  names included — they are valid if the user keeps them). */
    pendingName:
      rename.value !== null && rename.trimmed !== ""
        ? rename.trimmed
        : options.name,
    /** Surfaces a Save rejection on the name input (red + message). */
    openWithError: rename.openWithError,
  };
}

// The inline rename field — the design system's underline TextInput
// (Figma 14714-75897 "Untitled schema" row): transparent on the row, 32px
// tall, only a bottom border in the active input color. Error states move
// the underline to the status red.
const RenameInput = styled.input<{ $invalid: boolean }>`
  flex: 1;
  min-width: 0;
  height: 32px;
  font: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextBody};
  background: transparent;
  border: none;
  border-bottom: 1px solid
    ${(p) => (p.$invalid ? p.theme.statusError : p.theme.semanticMainPrimary)};
  border-radius: 0;
  padding: 0 ${designVariables.ui.input.paddingw}px;
  outline: none;
`;
